import { apiClient } from "../../../lib/axios";
import type {
  CreateUserRequest,
  CreateUserResponse,
  LoginRequest,
  LoginResponse,
  ChangePasswordRequest,
  ChangePasswordResponse,
  ChangePasswordTemporaryRequest,
  ChangePasswordTemporaryResponse,
  LogoutResponse,
  IsAuthenticatedResponse,
  CurrentUserResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  ConfirmForgotPasswordRequest,
  ConfirmForgotPasswordResponse,
  ConsentResponse,
  ConsentRequest,
  VerifyUserInviteRequest,
  VerifyUserInviteResponse,
  CompleteUserInviteRequest,
  CompleteUserInviteResponse,
  RequestUserInviteRequest,
  RequestUserInviteResponse,
  VerifyPasswordResetRequest,
  VerifyPasswordResetResponse,
  CompletePasswordResetRequest,
  CompletePasswordResetResponse,
} from "./types";

export const authApi = {
  createUser: async (data: CreateUserRequest): Promise<CreateUserResponse> => {
    const response = await apiClient.post("/auth/create-user", data);
    return response.data;
  },

  login: async (data: LoginRequest): Promise<LoginResponse> => {
    const response = await apiClient.post("/auth/", data);
    return response.data;
  },

  changePassword: async (
    data: ChangePasswordRequest
  ): Promise<ChangePasswordResponse> => {
    const response = await apiClient.post(
      "/auth/initial-change-password",
      data
    );
    return response.data;
  },

  changePasswordTemporary: async (
    data: ChangePasswordTemporaryRequest
  ): Promise<ChangePasswordTemporaryResponse> => {
    const response = await apiClient.post(
      "/auth/change-password-temporary",
      data
    );
    return response.data;
  },

  logout: async (): Promise<LogoutResponse> => {
    const response = await apiClient.post("/auth/logout");
    return response.data;
  },

  getMe: async () => {
    try {
      const authResponse = await apiClient.get<IsAuthenticatedResponse>(
        "/auth/is-authenticated"
      );
      const isAuthenticated =
        authResponse.data.message === "User is authenticated";

      if (!isAuthenticated) {
        return {
          authenticated: false,
          user: null,
          needs_new_password: false,
          needs_onboarding: false,
          needs_consent: false,
          consent_declined: false,
          is_admin: false,
        };
      }

      const userResponse =
        await apiClient.get<CurrentUserResponse>("/auth/current-user");
      const user = userResponse.data.data;

      const hasConsent = user?.consent?.decision === "consent";
      const hasDeclined = user?.consent?.decision === "decline";

      return {
        authenticated: isAuthenticated,
        user: isAuthenticated ? user : null,
        needs_new_password: false,
        needs_onboarding: user?.onboarding_required ?? false,
        needs_consent: !hasConsent && !hasDeclined,
        consent_declined: hasDeclined,
        is_admin: user?.is_admin ?? false,
      };
    } catch {
      return {
        authenticated: false,
        user: null,
        needs_new_password: false,
        needs_onboarding: false,
        needs_consent: false,
        consent_declined: false,
        is_admin: false,
      };
    }
  },

  isAuthenticated: async (): Promise<IsAuthenticatedResponse> => {
    const response = await apiClient.get("/auth/is-authenticated");
    return response.data;
  },

  getCurrentUser: async (): Promise<CurrentUserResponse> => {
    const response = await apiClient.get("/auth/current-user");
    return response.data;
  },

  forgotPassword: async (
    data: ForgotPasswordRequest
  ): Promise<ForgotPasswordResponse> => {
    const response = await apiClient.post("/auth/forgot-password", data);
    return response.data;
  },

  confirmForgotPassword: async (
    data: ConfirmForgotPasswordRequest
  ): Promise<ConfirmForgotPasswordResponse> => {
    const response = await apiClient.post(
      "/auth/confirm-forgot-password",
      data
    );
    return response.data;
  },

  updateConsent: async (data: ConsentRequest): Promise<ConsentResponse> => {
    const response = await apiClient.post("/consent", data);
    return response.data;
  },

  verifyUserInvite: async (
    data: VerifyUserInviteRequest
  ): Promise<VerifyUserInviteResponse> => {
    const response = await apiClient.post("/auth/verify-invite", data);
    return response.data;
  },

  completeUserInvite: async (
    data: CompleteUserInviteRequest
  ): Promise<CompleteUserInviteResponse> => {
    const response = await apiClient.post("/auth/complete-invite", data);
    return response.data;
  },

  requestUserInvite: async (
    data: RequestUserInviteRequest
  ): Promise<RequestUserInviteResponse> => {
    const response = await apiClient.post("/auth/request-invite", data);
    return response.data;
  },

  verifyPasswordReset: async (
    data: VerifyPasswordResetRequest
  ): Promise<VerifyPasswordResetResponse> => {
    const response = await apiClient.post("/auth/verify-password-reset", data);
    return response.data;
  },

  completePasswordReset: async (
    data: CompletePasswordResetRequest
  ): Promise<CompletePasswordResetResponse> => {
    const response = await apiClient.post("/auth/complete-password-reset", data);
    return response.data;
  },
};

export const getMe = authApi.getMe;
